import React, { memo, useCallback, useMemo, useEffect, useRef } from 'react';
import { Bot, Trash2, Pencil } from 'lucide-react';
import UserMenu from './UserMenu';
import { ChatSession, User } from '../../types';

import StatusPopover from '../../components/StatusPopover';

interface SidebarProps {
    user: User | null;
    currentSessionId: string | null;
    chatSessions: ChatSession[];
    isLoadingSessions: boolean;
    onSwitchToSession: (sessionId: string, sessionName: string) => void;
    onDeleteSession: (sessionId: string, sessionName: string) => void;
    onRenameSession?: (sessionId: string, newName: string) => void;
    onClearChat: () => void;
    onNewChat: () => void;
    onRefreshSessions?: () => void;   // 2026-09-18：↻ 刷新历史列表（对齐离线版，用户实测发现缺失）
    onLogout?: () => void;            // 2026-09-18：顶栏下线（生产无 header），用户菜单挪进侧栏
    onOpenSettings?: () => void;
}

const Sidebar: React.FC<SidebarProps> = memo(({
    user: _user,
    currentSessionId,
    chatSessions,
    isLoadingSessions,
    onSwitchToSession,
    onDeleteSession,
    onRenameSession,
    onClearChat,
    onNewChat,
    onRefreshSessions,
    onLogout,
    onOpenSettings
}) => {

    // ── 2026-09-18 用户令：侧栏分隔线可拖（宽度走 --sidebar-width，localStorage 记忆）──
    useEffect(() => {
        const saved = parseInt(localStorage.getItem('sidebar_width') || '');
        if (saved >= 200 && saved <= 480) {
            document.documentElement.style.setProperty('--sidebar-width', saved + 'px');
        }
    }, []);

    const startDrag = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const cur = parseInt(getComputedStyle(document.documentElement)
            .getPropertyValue('--sidebar-width')) || 280;
        const onMove = (ev: MouseEvent) => {
            const w = Math.min(480, Math.max(200, cur + ev.clientX - startX));
            document.documentElement.style.setProperty('--sidebar-width', w + 'px');
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            document.body.classList.remove('sidebar-dragging');
            const w = parseInt(getComputedStyle(document.documentElement)
                .getPropertyValue('--sidebar-width'));
            if (w) localStorage.setItem('sidebar_width', String(w));
        };
        document.body.classList.add('sidebar-dragging');
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, []);


    // 优化的会话列表渲染
    const sessionsList = useMemo(() => {
        if (isLoadingSessions && chatSessions.length === 0) {
            return (
                <div className="history-item">
                    <span>加载中...</span>
                </div>
            );
        }

        if (chatSessions.length === 0) {
            return (
                <div className="history-item">
                    <span>暂无历史记录</span>
                </div>
            );
        }

        // 序号已焊进 session_name（2026-09-24：存量 SQL + 后端命名 #N+1），显示层不再算
        return chatSessions.map(session => (
            <SessionItem
                key={session.id}
                session={session}
                isActive={currentSessionId === session.id}
                onSwitchToSession={onSwitchToSession}
                onDeleteSession={onDeleteSession}
                onRenameSession={onRenameSession}
            />
        ));
    }, [chatSessions, isLoadingSessions, currentSessionId, onSwitchToSession, onDeleteSession, onRenameSession]);

    // 2026-09-24 用户报：切换会话后列表弹回顶部，选中的会话要手滑才看得到
    const historyRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!currentSessionId) return;
        historyRef.current
            ?.querySelector('.history-item.active')
            ?.scrollIntoView({ block: 'nearest' });
    }, [currentSessionId, chatSessions]);

    return (
        <div className="sidebar">
            <div className="sidebar-header">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="ai-avatar">
                            <Bot size={24} />
                        </div>
                        <div>
                            <h2 style={{ margin: 0 }}>NTRK</h2>
                            <p className="subtitle" style={{ margin: 0 }}>NTRK审核</p>
                        </div>
                    </div>
                    <UserMenu user={_user} onLogout={onLogout!} onOpenSettings={onOpenSettings!} />
                </div>
            </div>

            <div className="chat-history" ref={historyRef}>
                <h3>聊天历史（{chatSessions.length}）
                    {onRefreshSessions && (
                        <button
                            className="icon-btn"
                            title="刷新历史列表"
                            onClick={(e) => { e.stopPropagation(); onRefreshSessions(); }}
                            style={{ marginLeft: 8, verticalAlign: 'middle', cursor: 'pointer',
                                     background: 'none', border: 'none', fontSize: 15, color: 'inherit', opacity: 0.8 }}
                        >↻</button>
                    )}
                </h3>

                {/* 当前会话 */}
                <div
                    className={`history-item ${!currentSessionId ? 'active' : ''}`}
                    onClick={onNewChat}
                >
                    <Bot size={16} />
                    <span>新对话</span>
                </div>

                {sessionsList}
            </div>

            {/* 分隔线拖柄（用户令 0918） */}
            <div className="sidebar-resizer" onMouseDown={startDrag} title="拖动调整侧栏宽度" />

            <div className="sidebar-footer">
                <button className="clear-chat-btn" onClick={onClearChat}>
                    <Trash2 size={18} />
                    清空聊天
                </button>
               <div className="model-info-row">
                    <span className="model-tag">Ver.1.0</span>
                 
                    <StatusPopover />
                </div>
            </div>
        </div>
    );
});

// 单独的会话项组件
const SessionItem: React.FC<{
    session: ChatSession;
    isActive: boolean;
    onSwitchToSession: (sessionId: string, sessionName: string) => void;
    onDeleteSession: (sessionId: string, sessionName: string) => void;
    onRenameSession?: (sessionId: string, newName: string) => void;
}> = memo(({ session, isActive, onSwitchToSession, onDeleteSession, onRenameSession }) => {
    const handleClick = useCallback((e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.delete-session-btn') && !target.closest('.rename-session-btn')) {
            onSwitchToSession(session.id, session.session_name);
        }
    }, [session.id, session.session_name, onSwitchToSession]);

    const handleDelete = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onDeleteSession(session.id, session.session_name || `会话 ${session.id}`);
    }, [session.id, session.session_name, onDeleteSession]);

    // 重命名（2026-09-17）：prompt 轻交互， Enter 确认/空串取消
    const handleRename = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!onRenameSession) return;
        const cur = session.session_name || '';
        const name = window.prompt('重命名会话', cur);
        if (name && name.trim() && name.trim() !== cur) {
            onRenameSession(session.id, name.trim());
        }
    }, [session.id, session.session_name, onRenameSession]);

    const formattedDate = useMemo(() => {
        return new Date(session.created_at).toLocaleDateString();
    }, [session.created_at]);

    return (
        <div
            className={`history-item ${isActive ? 'active' : ''}`}
            onClick={handleClick}
        >
            <Bot size={16} />
            <span>{session.session_name || `会话 ${session.id}`}</span>
            <span className="session-date">
                {formattedDate}
            </span>
            {onRenameSession && (
                <button
                    className="rename-session-btn"
                    onClick={handleRename}
                    title="重命名此会话"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'inherit', opacity: 0.6 }}
                >
                    <Pencil size={14} />
                </button>
            )}
            <button
                className="delete-session-btn"
                onClick={handleDelete}
                title="删除此会话"
            >
                <Trash2 size={14} />
            </button>
        </div>
    );
});

SessionItem.displayName = 'SessionItem';
Sidebar.displayName = 'Sidebar';

export default Sidebar;
